import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { env } from '../../../../environments/environment';

/** Slug ammessi dal CRM per il campo `lead_requestreason`. */
export type CrmRequestReason =
  | 'info-generali'
  | 'supporto-adesione'
  | 'collaborazione'
  | 'iscrizione-alla-newsletter'
  | 'partecipazione-eventi'
  | 'assistenza-tecnica';

/** Dati raccolti dal form di contatto. */
export interface CrmContactPayload {
  name: string;
  surname: string;
  email: string;
  entity: string;
  requestReason: CrmRequestReason;
  message: string;
  privacy: boolean;
}

/** Esito normalizzato della chiamata al CRM. */
export interface CrmContactResult {
  success: boolean;
  /** Errori per campo restituiti dal CRM (HTTP 406). */
  fieldErrors?: Record<string, string>;
  /** Messaggio generico (successo o errore). */
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class CrmContactService {
  readonly #http = inject(HttpClient);
  readonly #crm = env.crm;

  sendLead(payload: CrmContactPayload): Observable<CrmContactResult> {
    // Header CRM (Authorization, Request-Id, Request-Url) aggiunti dal proxy
    // nginx (/api/crm/) o da proxy.conf.json in locale — non dal browser (CORS).
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    const body = {
      lead_name: payload.name,
      lead_surname: payload.surname,
      lead_email: payload.email,
      lead_entity: payload.entity,
      lead_requestreason: payload.requestReason,
      lead_message: payload.message,
      lead_privacy: payload.privacy ? 'on' : '',
      lead_form: this.#crm.leadForm,
    };

    return this.#http
      .post<{ success?: string; error?: unknown }>(this.#crm.endpoint, body, { headers })
      .pipe(
        map(() => ({ success: true } as CrmContactResult)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 406 && err.error?.error && typeof err.error.error === 'object') {
            return of<CrmContactResult>({
              success: false,
              fieldErrors: err.error.error as Record<string, string>,
              message: 'Alcuni campi non sono validi. Controlla i dati inseriti.',
            });
          }

          if (err.status === 401) {
            return throwError(() => ({
              success: false,
              message: 'Autorizzazione non valida verso il servizio CRM.',
            } as CrmContactResult));
          }

          return throwError(() => ({
            success: false,
            message: "Si è verificato un errore durante l'invio della richiesta. Riprova più tardi.",
          } as CrmContactResult));
        }),
      );
  }
}
